import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import {
  DirectFileUploader,
  FileUploadState,
} from '../../sidebar/DirectFileUploader';
import './CloseTaskModal.scss';

interface CloseTaskModalProps {
  show: boolean;
  taskId: string;
  acceptedAt?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function computeDefaultMinutes(acceptedAt?: string | null): number | '' {
  if (!acceptedAt) return '';
  const accepted = new Date(acceptedAt).getTime();
  if (isNaN(accepted)) return '';
  const diffMs = Date.now() - accepted;
  if (diffMs <= 0) return 1;
  return Math.ceil(diffMs / 60000);
}

const CloseTaskModal: React.FC<CloseTaskModalProps> = ({
  show,
  taskId,
  acceptedAt,
  onClose,
  onSuccess,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [notes, setNotes] = useState('');
  const [timeSpentMinutes, setTimeSpentMinutes] = useState<number | ''>(
    computeDefaultMinutes(acceptedAt),
  );
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadState[]>([]);
  const [bypassAssistant, setBypassAssistant] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recompute default time each time the modal opens
  useEffect(() => {
    if (show) {
      setTimeSpentMinutes(computeDefaultMinutes(acceptedAt));
    }
  }, [show, acceptedAt]);

  const handleFileUpload = useCallback((fileState: FileUploadState) => {
    setUploadedFiles((prev) => {
      const existing = prev.findIndex((f) => f.id === fileState.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = fileState;
        return updated;
      }
      return [...prev, fileState];
    });
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!notes.trim()) {
      setError('Please provide notes.');
      return;
    }
    if (!timeSpentMinutes || timeSpentMinutes < 1) {
      setError('Time spent must be at least 1 minute.');
      return;
    }

    const pendingUploads = uploadedFiles.some((f) => f.status === 'uploading');
    if (pendingUploads) {
      setError('Please wait for file uploads to finish.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const imageUrls = uploadedFiles
        .filter((f) => f.status === 'success' && f.url)
        .map((f) => f.url!);

      await api.post(
        `/human-tasks/${taskId}/close`,
        {
          notes: notes.trim(),
          timeSpentMinutes,
          ...(imageUrls.length > 0 && { imageUrls }),
          bypassAssistant,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      onSuccess();
      handleReset();
    } catch (err: any) {
      const msg =
        err.response?.data?.error || 'Failed to close task. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [
    notes,
    timeSpentMinutes,
    uploadedFiles,
    bypassAssistant,
    taskId,
    getAccessTokenSilently,
    onSuccess,
  ]);

  const handleReset = useCallback(() => {
    setNotes('');
    setTimeSpentMinutes(computeDefaultMinutes(acceptedAt));
    setUploadedFiles([]);
    setBypassAssistant(false);
    setError(null);
  }, [acceptedAt]);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [onClose, handleReset]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className='close-task-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>Close Task</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='notes-section'>
          <label className='field-label'>What happened?</label>
          <textarea
            className='notes-input'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder='Describe what happened...'
            rows={4}
          />
        </div>

        <div className='time-section'>
          <label className='field-label'>Time spent (minutes)</label>
          <input
            type='number'
            className='time-input'
            value={timeSpentMinutes}
            onChange={(e) => {
              const val = e.target.value;
              setTimeSpentMinutes(
                val === '' ? '' : Math.max(1, parseInt(val, 10) || 1),
              );
            }}
            min={1}
            placeholder='e.g. 15'
          />
        </div>

        <div className='files-section'>
          <label className='field-label'>
            Attachments <span className='optional'>(optional)</span>
          </label>
          {uploadedFiles.length > 0 && (
            <div className='uploaded-files'>
              {uploadedFiles.map((file) => (
                <div key={file.id} className={`uploaded-file ${file.status}`}>
                  {file.previewUrl && (
                    <img
                      src={file.previewUrl}
                      alt={file.name}
                      className='file-preview'
                    />
                  )}
                  <span className='file-name'>{file.name}</span>
                  {file.status === 'uploading' && (
                    <span className='file-status'>Uploading...</span>
                  )}
                  {file.status === 'error' && (
                    <span className='file-status error'>Failed</span>
                  )}
                  <button
                    type='button'
                    className='remove-file-btn'
                    onClick={() => handleRemoveFile(file.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <DirectFileUploader
            onFileUpload={handleFileUpload}
            disabled={submitting}
          />
        </div>

        <div className='bypass-section'>
          <label className='bypass-label'>
            <input
              type='checkbox'
              checked={bypassAssistant}
              onChange={(e) => setBypassAssistant(e.target.checked)}
            />
            <span className='bypass-text'>
              Skip assistant review (closes directly, less context forwarded)
            </span>
          </label>
        </div>

        {error && <div className='error-message'>{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='outline-secondary'
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={submitting || !notes.trim() || !timeSpentMinutes}
        >
          {submitting ? 'Submitting...' : 'Close Task'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CloseTaskModal;
